import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DsSharedModule } from '../../../../common/ds-shared.module';
import { ArtworkStatusTone } from '../../../../common/ds-artwork-card/ds-artwork-card.component';
import { SiteContent, Section, SizeKey, SiteWork, isElementHidden } from '../../../../models/site-content.model';
import { DsArtworkEnquiryComponent } from '../../../../common/ds-artwork-enquiry/ds-artwork-enquiry.component';
import { ArtworkEnquiryService } from '../../../../Services/shared/artwork-enquiry.service';

/**
 * Read-only renderer for a published artist website.
 *
 * The same `SiteContent` the builder edits, rendered without any editing affordance — no
 * contenteditable, no section toolbars, no pickers. This is what a visitor sees.
 *
 * It deliberately reuses the builder's stylesheet rather than forking it: one source of truth
 * for the three looks means the published page cannot drift from what the artist was shown while
 * building it. Unused builder-chrome rules are inert here (styles are component-scoped), and the
 * wrapper element chain is reproduced so ancestor selectors still match.
 *
 * Text is rendered with plain interpolation. The builder's write-once `CeModelDirective` exists
 * only to stop change detection clobbering someone's typing — with nothing to type into, normal
 * binding is correct. Rich-text fields go through `[innerHTML]`, which Angular sanitizes.
 */
@Component({
  selector: 'app-site-view',
  standalone: true,
  imports: [CommonModule, FormsModule, DsSharedModule, DsArtworkEnquiryComponent],
  templateUrl: './site-view.component.html',
  styleUrls: ['../website-builder.component.scss'],
})
export class SiteViewComponent {

  constructor(private enquiries: ArtworkEnquiryService) {}

  /** The published document. */
  @Input() content!: SiteContent;

  /** Artworks referenced by the site's Work sections, already filtered to the shown ids. */
  @Input() works: SiteWork[] = [];

  /** Currency for price badges. */
  @Input() currency = 'USD';

  /**
   * The published site's slug. Sent with an enquiry so the server can resolve the recipient — the
   * artist is never named by the client, which is what stops a message being addressed to an
   * arbitrary artist.
   */
  @Input() identifier = '';

  private blockWidths: Record<SizeKey, string> = { sm: '46%', md: '66%', lg: '82%', full: '100%' };
  private heroWidths: Record<SizeKey, string> = { sm: '45%', md: '68%', lg: '90%', full: '100%' };

  // These four mirror WebsiteBuilderComponent exactly. They are duplicated rather than shared
  // because the builder is a large stateful component and importing it here would drag its whole
  // editing surface into the public bundle — but they must stay in step, or a published page
  // would render at different widths or badge a piece differently from the builder preview.

  /** Look + font + accent, applied as CSS custom properties. */
  public get siteStyle(): { [k: string]: string } {
    const s: { [k: string]: string } = {};
    if (this.content?.accent) { s['--accent'] = this.content.accent; }
    if (this.content?.hfont) { s['--hfont'] = this.content.hfont; }
    if (this.content?.bfont) { s['--bfont'] = this.content.bfont; }
    return s;
  }

  public secWidth(sec: Section): string {
    return this.blockWidths[sec.width || 'full'];
  }

  public heroWidth(s: SizeKey): string {
    return this.heroWidths[s] || '90%';
  }

  /**
   * State for the CONTACT SECTION's inline form only.
   *
   * The detail and enquiry modals moved to `ds-artwork-enquiry`, which owns their state. This
   * form stays here because it is styled by the artist's chosen look through ancestor selectors,
   * which does not survive being moved into an encapsulated component. The SENDING is shared —
   * see ArtworkEnquiryService — so the two cannot drift on what actually reaches the artist.
   */
  public enquiry = { name: '', email: '', message: '' };
  public enquirySent = false;
  public enquirySending = false;
  public enquiryError = '';

  /** The sections a visitor sees — the artist's hidden ones are kept in the document, not shown. */
  public get visibleSections(): Section[] {
    return (this.content?.sections || []).filter((s) => !s.hidden);
  }

  /** False when the artist has switched this part of the section off. */
  public shows(sec: Section, key: string): boolean {
    return !isElementHidden(sec, key);
  }

  /**
   * The contact section's data, wherever it sits on the page.
   *
   * An enquiry about a piece is raised from the work grid, which may be a long way from the
   * contact section — but it still needs the artist's address, so it is looked up rather than
   * passed down.
   *
   * Deliberately searches HIDDEN sections too. Hiding the contact block hides the block; it does
   * not switch enquiries off. When deleting it was possible, it did exactly that, on every artwork,
   * with nothing in the builder to say so.
   */
  public get contactData(): any {
    const sec = (this.content?.sections || []).find((s) => s.type === 'contact');
    return sec ? sec.data : null;
  }

  /** No address, no enquiry: the button is hidden rather than offered and then failing. */
  public get canEnquire(): boolean {
    return !!(this.contactData && this.contactData.email);
  }


  /** For the footer copyright line. */
  public readonly year = new Date().getFullYear();

  /**
   * Link back to ArtFlow Pro, tagged by placement.
   *
   * Artists get the website free; visible attribution is what ArtFlow gets back. Tagging the
   * placement means that return can be measured rather than assumed — badge and footer will
   * convert very differently, and without this there is no way to know which is carrying it, or
   * whether the badge earns the space it takes on the artist's page.
   */
  public afpLink(placement: 'badge' | 'footer'): string {
    return 'https://www.artflow-pro.com/?utm_source=artist-website'
      + '&utm_medium=' + placement
      + '&utm_campaign=website-builder';
  }

  /** Tidied rich text, memoised by its source string — templates re-evaluate on every cycle. */

  /** Social/other links the artist filled in, in a fixed order, skipping the blanks. */
  public socials(data: any): Array<{ label: string; url: string }> {
    const out: Array<{ label: string; url: string }> = [];
    const add = (label: string, raw: string) => {
      const url = this.externalUrl(raw);
      if (url) { out.push({ label, url }); }
    };
    add('Instagram', data?.instagram);
    add('Facebook', data?.facebook);
    add('LinkedIn', data?.linkedin);
    add('Website', data?.website);
    return out;
  }

  /**
   * Normalises a link the artist typed.
   *
   * They paste "instagram.com/frances" at least as often as a full URL, and without a scheme the
   * browser reads it as a relative path — sending a visitor to a 404 inside the site rather than
   * to Instagram. Anything that does not look like a host at all is dropped rather than guessed.
   */
  private externalUrl(raw: string | null | undefined): string {
    const v = (raw || '').trim();
    if (!v) { return ''; }
    if (/^https?:\/\//i.test(v)) { return v; }
    if (/^(www\.|[a-z0-9-]+\.[a-z]{2,})/i.test(v)) { return 'https://' + v; }
    return '';
  }



  /** `tel:` cannot cope with spaces or brackets, which is how people write phone numbers. */
  public telHref(phone: string): string {
    return (phone || '').replace(/[^\d+]/g, '');
  }

  /**
   * Sends the CONTACT SECTION's inline enquiry.
   *
   * The request shape, the "HTTP 200 is not success" handling and the mailto fallback all live
   * in ArtworkEnquiryService, shared with the artwork panel. Three copies of this logic is how
   * an artist ends up receiving enquiries that do not say which piece they are about.
   */
  public sendEnquiry(ev: Event): void {
    ev.preventDefault();
    if (this.enquirySending || !this.enquiries.isSendable(this.enquiry)) { return; }

    this.enquirySending = true;
    this.enquiryError = '';

    // null: raised from the contact section, so it is about the site rather than a piece.
    this.enquiries.send(this.identifier, this.enquiry, null).subscribe((outcome) => {
      this.enquirySending = false;

      if (!outcome.ok) {
        this.enquiryError = outcome.error;
        return;
      }

      this.enquirySent = true;
      this.enquiry = { name: '', email: '', message: '' };
    });
  }

  /** Offered only when sending fails, so a visitor never loses what they typed. */
  public mailtoFallback(): string {
    return this.enquiries.mailto(
      this.contactData?.email, this.content?.siteTitle || '', this.enquiry, null);
  }

  /**
   * Rich text is rendered exactly as stored — no cleaning on the way out.
   *
   * This used to run the sanitiser on every render, which was wrong twice over. It made the
   * published page differ from what the artist saw while building (the builder renders the raw
   * value), and it re-ran a lossy regex transform over content it could not reliably interpret —
   * so a formatting choice could disappear for reasons no part of the code pointed at.
   *
   * Cleaning is now a one-time action the artist takes: "Tidy pasted text" rewrites the stored
   * value and marks it `dsTidied`. What is stored is what renders, in both places, permanently.
   *
   * Angular still sanitizes at the `[innerHTML]` binding — that is the security boundary, and it
   * is unaffected by any of this.
   */
  public rich(raw: string | null | undefined): string {
    return raw || '';
  }

  /**
   * The pieces one Work section shows, in the order the artist chose them.
   *
   * Selection is per section — a site can carry several galleries ("Latest work", "Vessels",
   * "Sold archive") and each is curated separately. `works` is every piece used anywhere on the
   * site; a section picks its own out of that by id.
   *
   * Sites published before galleries became independent have no per-section list, so they fall
   * back to showing everything — which is exactly how they looked when they were published.
   */
  public worksFor(sec: Section): SiteWork[] {
    const ids: string[] = Array.isArray(sec.data?.shownIds) ? sec.data.shownIds : [];
    if (!ids.length) { return this.works; }

    const byId = new Map(this.works.map((w) => [w.id, w] as [string, SiteWork]));
    return ids.map((id) => byId.get(id)).filter((w): w is SiteWork => !!w);
  }

  /** Column count for a text block, as chosen by the artist. */
  public columnsClass(sec: Section): string {
    return 'cols-' + (sec.data?.columns === 2 ? '2' : '1');
  }

  /**
   * True when a field has visible content.
   *
   * A plain truthiness check is not enough for the rich-text fields: a contenteditable the artist
   * typed into and then cleared comes back as `<br>`, `<div><br></div>` or `&nbsp;` — non-empty
   * strings that render as nothing. Without this, "hide it when there's no text" would still
   * publish an empty heading block and its margins.
   */
  public hasText(value: string | null | undefined): boolean {
    if (!value) { return false; }
    return String(value)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim().length > 0;
  }

  public statusOf(w: SiteWork): string {
    return w.sold ? 'Sold' : (w.consigned ? 'On consignment' : 'Available');
  }

  public toneOf(w: SiteWork): ArtworkStatusTone {
    return w.sold ? 'sold' : (w.consigned ? 'consigned' : 'available');
  }
}
